import { computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import {
  SIDEBAR_SORT_SECTIONS,
  getSidebarSortOptions,
  resolveSidebarSort,
  sortSidebarItems,
} from 'dashboard/helper/sidebarSort';

/**
 * What the sidebar menu is built from: the account's inboxes, teams, labels and saved views, their unread
 * counts, the user's sort preferences and the feature flags that add or remove items. Moved out of
 * Sidebar.vue as it was; the menu itself is built in useSidebarMenu.js.
 */
export function useSidebarData() {
  const store = useStore();

  const accountId = useMapGetter('getCurrentAccountId');
  const currentUserId = useMapGetter('getCurrentUserID');
  const isFeatureEnabledonAccount = useMapGetter(
    'accounts/isFeatureEnabledonAccount'
  );

  const hasAdvancedAssignment = computed(() => {
    return isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.ADVANCED_ASSIGNMENT
    );
  });

  const hasConversationUnreadCounts = computed(() => {
    return isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.CONVERSATION_UNREAD_COUNTS
    );
  });

  const hasFilteredUnreadCounts = computed(() => {
    return (
      hasConversationUnreadCounts.value &&
      isFeatureEnabledonAccount.value(
        accountId.value,
        FEATURE_FLAGS.UNREAD_COUNT_FOR_FILTERS
      )
    );
  });

  const hasDataImport = computed(() => {
    return isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.DATA_IMPORT
    );
  });

  const fetchConversationUnreadCounts = ([currentAccountId, isEnabled]) => {
    if (!currentAccountId) return;

    if (!isEnabled) {
      store.dispatch('conversationUnreadCounts/clear');
      return;
    }

    store.dispatch('conversationUnreadCounts/get');
  };

  const fetchSidebarSortPreferences = ([currentAccountId, userId]) => {
    if (!currentAccountId || !userId) return;
    store.dispatch('sidebarSortPreferences/initialize');
  };

  const inboxes = useMapGetter('inboxes/getInboxes');
  const labels = useMapGetter('labels/getLabelsOnSidebar');
  const getInboxUnreadCount = useMapGetter(
    'conversationUnreadCounts/getInboxUnreadCount'
  );
  const getLabelUnreadCount = useMapGetter(
    'conversationUnreadCounts/getLabelUnreadCount'
  );
  const getTeamUnreadCount = useMapGetter(
    'conversationUnreadCounts/getTeamUnreadCount'
  );
  const mentionsUnreadCount = useMapGetter(
    'conversationUnreadCounts/getMentionsUnreadCount'
  );
  const participatingUnreadCount = useMapGetter(
    'conversationUnreadCounts/getParticipatingUnreadCount'
  );
  const unattendedUnreadCount = useMapGetter(
    'conversationUnreadCounts/getUnattendedUnreadCount'
  );
  const getFolderUnreadCount = useMapGetter(
    'conversationUnreadCounts/getFolderUnreadCount'
  );
  const teams = useMapGetter('teams/getMyTeams');
  const contactCustomViews = useMapGetter('customViews/getContactCustomViews');
  const conversationCustomViews = useMapGetter(
    'customViews/getConversationCustomViews'
  );
  const getSidebarSectionSort = useMapGetter(
    'sidebarSortPreferences/getSectionSort'
  );

  onMounted(() => {
    store.dispatch('labels/get');
    store.dispatch('inboxes/get');
    store.dispatch('notifications/unReadCount');
    store.dispatch('teams/get');
    store.dispatch('attributes/get');
    store.dispatch('customViews/get', 'conversation');
    store.dispatch('customViews/get', 'contact');
  });

  watch(
    [accountId, hasConversationUnreadCounts],
    fetchConversationUnreadCounts,
    { immediate: true }
  );

  watch([accountId, currentUserId], fetchSidebarSortPreferences, {
    immediate: true,
  });

  const hasUnreadCountsForSection = section => {
    if (section === SIDEBAR_SORT_SECTIONS.FOLDERS) {
      return hasFilteredUnreadCounts.value;
    }

    return hasConversationUnreadCounts.value;
  };

  const getSortOptionsForSection = section =>
    getSidebarSortOptions(section, {
      hasUnreadCounts: hasUnreadCountsForSection(section),
    });

  const getSortForSection = section =>
    resolveSidebarSort(section, getSidebarSectionSort.value(section), {
      hasUnreadCounts: hasUnreadCountsForSection(section),
    });

  const updateSortPreference = (section, sortBy) => {
    store.dispatch('sidebarSortPreferences/setSectionSort', {
      section,
      sortBy,
    });
  };

  const buildSortConfig = section => ({
    sortOptions: getSortOptionsForSection(section),
    activeSort: getSortForSection(section),
    onSortChange: sortBy => updateSortPreference(section, sortBy),
  });

  const sortedFolders = computed(() =>
    sortSidebarItems(conversationCustomViews.value, {
      sortBy: getSortForSection(SIDEBAR_SORT_SECTIONS.FOLDERS),
      labelKey: view => view.name,
      unreadCountKey: view => getFolderUnreadCount.value(view.id),
    })
  );

  const sortedTeams = computed(() =>
    sortSidebarItems(teams.value, {
      sortBy: getSortForSection(SIDEBAR_SORT_SECTIONS.TEAMS),
      labelKey: team => team.name,
      unreadCountKey: team => getTeamUnreadCount.value(team.id),
    })
  );

  const sortedInboxes = computed(() =>
    sortSidebarItems(inboxes.value, {
      sortBy: getSortForSection(SIDEBAR_SORT_SECTIONS.CHANNELS),
      labelKey: inbox => inbox.name,
      unreadCountKey: inbox => getInboxUnreadCount.value(inbox.id),
    })
  );

  const sortedLabels = computed(() =>
    sortSidebarItems(labels.value, {
      sortBy: getSortForSection(SIDEBAR_SORT_SECTIONS.LABELS),
      labelKey: label => label.title,
      unreadCountKey: label => getLabelUnreadCount.value(label.id),
    })
  );

  return {
    hasAdvancedAssignment,
    hasFilteredUnreadCounts,
    hasDataImport,
    labels,
    getInboxUnreadCount,
    getLabelUnreadCount,
    getTeamUnreadCount,
    mentionsUnreadCount,
    participatingUnreadCount,
    unattendedUnreadCount,
    getFolderUnreadCount,
    contactCustomViews,
    buildSortConfig,
    sortedFolders,
    sortedTeams,
    sortedInboxes,
    sortedLabels,
  };
}
